import {
  HelpOutlined as HelpOutlineIcon,
  MoneyOff as MoneyOffIcon,
  MoreVert as MoreVertIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import { MenuButton, type MenuButtonRef } from "@msys/ui";
import React from "react";
import type {
  ContractType,
  DocType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useContextMenu } from "../../commons/hooks/useContextMenu.js";
import { ConfirmModalProps } from "../../commons/modals/ConfirmProcess.js";
import { ExpandedStore } from "../../trees-virtual/hooks/useExpandedStore.js";
import { AdditionalItemInput } from "../../trees-virtual/types.js";
import { RIGHT_ICON_STYLE } from "../../trees/BareTreeTableItem.js";
import {
  TreeDataIconsWrapper,
  TreeDataPath,
} from "../../trees/components/TreeDataContainer.js";
import { TreeTitle } from "../../trees/components/TreeTitle.js";
import { DocItemContextMenuBatchItems } from "../doc-items/menus/DocItemContextMenuBatchItems.js";
import { DocItemContextMenuItems } from "../doc-items/menus/DocItemContextMenuItems.js";
import type { ItemSelect_ItemFragment } from "../doc-items/ItemSelect.generated.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type {
  DocDataGridItemComponent,
  DocDataGridItemComponentProps,
} from "./DocDataGrid.js";
import { DocDataGridSelectionContext } from "./DocDataGridSelectionContext.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreateDocDataGridItemCellOptions<T extends DocDataGrid_ItemFragment> {
  pathToDocPage: string;
  // `null` for templates (no project); flows to DocItemContextMenuItems, which
  // already accepts a null projectId.
  projectId: string | null;
  doc: {
    id: string;
    rootItemId: string;
    viewerPermissions: PermissionName[];
    owningSystemOrganisationId: string;
    contractType: ContractType;
    type: DocType;
  };
  startConfirmProcess: (
    props: ConfirmModalProps
  ) => Promise<boolean | undefined>;
  navigateToItem: (id: string) => void;
  expandedStore: ExpandedStore;
  pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
  isAllowedToPasteItem: (item: DocDataGrid_ItemFragment) => boolean;
  onAdditionalItemInputChange: ((input: AdditionalItemInput) => void) | null;
  additionalMenuItems?: (item: T) => React.ReactNode;
  getValidMoveTargets: (
    itemIds: string[]
  ) => Promise<ItemSelect_ItemFragment[]>;
  onMoveItems: (
    targetItemId: string,
    itemIds: string[]
  ) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createDocDataGridItemCell<T extends DocDataGrid_ItemFragment>({
  pathToDocPage,
  projectId,
  doc,
  startConfirmProcess,
  navigateToItem,
  expandedStore,
  pasteItem,
  isAllowedToPasteItem,
  onAdditionalItemInputChange,
  additionalMenuItems,
  getValidMoveTargets,
  onMoveItems,
}: CreateDocDataGridItemCellOptions<T>): DocDataGridItemComponent<T> {
  const DocDataGridItemCell = ({
    item,
    row,
    parentItem,
  }: DocDataGridItemComponentProps<T>) => {
    const theme = useTheme();
    const showHelpIcon =
      item.type === "decision" || item.decisionIsContingentItem;

    const { checkedItemIds, isItemChecked } = React.useContext(
      DocDataGridSelectionContext
    );

    const showBatchMenu = isItemChecked(item.id);

    const closeMenuRef = React.useRef<() => void>(() => {});
    const menuButtonRef = React.useRef<MenuButtonRef>(null);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    const menuItems = showBatchMenu ? (
      <DocItemContextMenuBatchItems
        getValidMoveTargets={() => getValidMoveTargets(checkedItemIds)}
        onMoveItems={targetItemId => onMoveItems(targetItemId, checkedItemIds)}
        onMoved={() => closeMenuRef.current()}
      />
    ) : (
      <>
        {additionalMenuItems?.(item)}
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={doc.id}
          docType={doc.type}
          projectId={projectId}
          item={item}
          itemParentType={parentItem?.type ?? null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedStore.expandedItemIds}
          pasteItem={isAllowedToPasteItem(item) ? pasteItem : null}
          onAdditionalItemInputChange={onAdditionalItemInputChange}
          getValidMoveTargets={() => getValidMoveTargets([item.id])}
          onMoveItem={targetItemId => onMoveItems(targetItemId, [item.id])}
          onMoved={() => closeMenuRef.current()}
        />
      </>
    );

    const [handleContextMenu, menuElement, closeContextMenu] =
      useContextMenu<HTMLDivElement>(
        <Box sx={{ minWidth: theme.layout.filters.popoverWidth.md }}>
          {menuItems}
        </Box>
      );

    closeMenuRef.current = () => {
      closeContextMenu();
      menuButtonRef.current?.close();
    };

    return (
      <Box
        onClick={() => {
          navigateToItem(item.id);
          expandedStore.setItemExpanded(item.id, true);
        }}
        onContextMenuCapture={handleContextMenu}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: 0.5,
          cursor: "pointer",
          overflow: "hidden",

          ".doc-data-grid-item-menu-button": {
            display: isMenuOpen ? "block" : "none",
          },

          "&:hover .doc-data-grid-item-menu-button": { display: "block" },
        }}
      >
        {showHelpIcon && (
          <HelpOutlineIcon
            fontSize="small"
            style={{ color: theme.palette.warning.main, flexShrink: 0 }}
          />
        )}
        <TreeTitle
          $bold={item.type === "section"}
          $depth={row.depth}
          style={{ flex: 1, minWidth: 0 }}
        >
          {item.pathForPdf && (
            <TreeDataPath $depth={row.depth}>{item.pathForPdf}</TreeDataPath>
          )}
          {item.title}
        </TreeTitle>
        <Box
          className="doc-data-grid-item-menu-button"
          sx={{ flexShrink: 0, flexGrow: 0 }}
        >
          <MenuButton
            ref={menuButtonRef}
            Icon={<MoreVertIcon fontSize="small" />}
            onToggle={setIsMenuOpen}
          >
            {menuItems}
          </MenuButton>
        </Box>
        <TreeDataIconsWrapper>
          {item.isHidden && (
            <span>
              <VisibilityOffIcon style={RIGHT_ICON_STYLE} />
            </span>
          )}
          {item.isPriceHidden && !item.isHidden && (
            <span>
              <MoneyOffIcon style={RIGHT_ICON_STYLE} />
            </span>
          )}
        </TreeDataIconsWrapper>
        {menuElement}
      </Box>
    );
  };

  DocDataGridItemCell.displayName = "DocDataGridItemCell";

  return DocDataGridItemCell;
}
